import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  Typography,
  Divider,
  Checkbox,
  Form,
  Input,
  DatePicker,
  Select,
} from "antd";
import { AnchorLinkItemProps } from "antd/es/anchor/Anchor";
import { ActionButton } from "@hdi-seguros/web-components.action-button";
import { Card } from "@hdi-seguros/web-components.card";

import {
  AseguradoPoliza,
  DECISION_TYPE,
  FILE_TYPES,
  InsurancePolicy,
  LifeCoverageCodes,
  LifePossession,
  Possessions,
  SelectOption,
} from "../../../../../shared/interfaces";
import {
  Column,
  _renderColumns,
  _renderSection,
} from "../../utils/renderSections";
import { COLORS } from "../../../../../config/themes/colors";
import {
  ErrorResults,
  SectionsSkeleton,
  Anchor,
  RowSize,
  ColSize,
} from "../../../../../shared/components";
import { DeleteOutlined } from "@ant-design/icons";
import { InsuredRegistrationForm } from "../InsuredRegistrationForm";
import { MasterContext, UIContext } from "../../../../../shared/contexts";

import "./index.css";

import dayjs from "dayjs";
import DocumentsService from "../../../../../shared/services/documents";
import {
  displayErrorMessage,
  downloadBase64Files,
  formattedPrice,
} from "../../../../../shared/utils";
import {
  CoberagePolicyEnrollmentAndTermination,
  getEnrollmentAndTerminationOfInsuredDocumentRequest,
  InsuredList,
} from "../../../../../shared/interfaces/services/enrollment-and-termination-document-request";
import EmailService, {
  buildSendMailRequest,
} from "../../../../../shared/services/email";
import {
  ContentTypeValue,
  DistributionListKey,
  Recipients,
} from "../../../../../shared/interfaces/services/email-services";
import QuotationService from "../../../../../shared/services/quotation";
import { buildEmailBody } from "./utils/buildEmailBody";

interface PayrollTabProps {
  insurancePolicyDetail?: InsurancePolicy;
  insuredsByPolicy?: AseguradoPoliza;
  possessionsList?: Array<Possessions>;
}

interface Insured {
  fullName: string;
  documentType: string;
  documentNumber: string;
  birthDate: string;
  cuil: string;
  gender: string;
  activities: string;
  newInsured: boolean;
  coberturas?: Array<{ nombreCob: string; sumaCob: string }>;
  sumAsegurada?: string;
}

const MOCK_SECTIONS: Array<AnchorLinkItemProps> = [
  {
    key: "datos-del-cliente",
    href: "#datos-del-cliente",
    title: "Datos del Cliente o Tomador",
  },
  {
    key: "vigencia",
    href: "#vigencia",
    title: "Vigencia",
  },
  {
    key: "datos-del-beneficiario",
    href: "#datos-del-beneficiario",
    title: "Datos del beneficiario",
  },
  {
    key: "datos-del-asegurado",
    href: "#datos-del-asegurado",
    title: "Datos del asegurado",
  },
  {
    key: "alta-de-asegurado",
    href: "#alta-de-asegurado",
    title: "Alta de asegurado",
  },
];

const _renderInsuredCards = (columns: Array<Column>, index: number) => (
  <div key={index}>
    <Card style={{ marginBottom: 20 }}>
      <RowSize rowProps={{ gutter: [0, 15] }}>
        {_renderColumns(columns)}
      </RowSize>
    </Card>
  </div>
);

export const PayrollTab: FC<PayrollTabProps> = ({
  insurancePolicyDetail,
  insuredsByPolicy,
  possessionsList,
}) => {
  const { isLoading, showError } = useContext(UIContext);

  const [insureds, setInsureds] = useState<Array<Insured>>([]);
  const [insuredsDeleteds, setInsuredsDeleteds] = useState<Array<Insured>>([]);
  const [insuredsMarkedForDeletion, setInsuredsMarkedForDeletion] = useState<
    Set<string>
  >(new Set());
  const [insured, setInsured] = useState<any>();
  const [clientDataForm, setClientDataForm] = useState<Column[]>([]);
  const [beneficiaryDataForm, setBeneficiaryDataForm] = useState<Column[]>([]);
  const [beneficiaryTaker, setBeneficiaryTaker] = useState<boolean>(true);
  const [hasRepeticionClausule, setHasRepeticionClausule] =
    useState<boolean>(false);
  const [clauseText, setClauseText] = useState<string>("");
  const [beneficiaryName, setBeneficiaryName] = useState<string>("");
  const [beneficiaryDocument, setBeneficiaryDocument] = useState<string>("");
  const [apActiviesListOptions, setApActivitiesListOptions] = useState<
    Array<SelectOption>
  >([]);
  const [disabledButtons, setDisabledButtons] = useState<boolean>(false);

  const filterEnrollmentCoverages = useCallback(
    (coverages: LifePossession["coberturas"] = []) =>
      coverages.filter(
        ({ codCobert }) => codCobert !== LifeCoverageCodes.ADDITIONAL_COVERAGE
      ),
    []
  );

  const [beneficiaryPersonType, setBeneficiaryPersonType] =
    useState<string>("");

  const personTypeMock: SelectOption[] = useMemo(
    () => [
      {
        label: "Persona Fisica",
        value: "F",
      },
      {
        label: "Persona Juridica",
        value: "J",
      },
    ],
    []
  );
  const [vigenciaDesde, setVigenciaDesde] = useState<any>(
    insurancePolicyDetail?.fechaVigDesd
      ? dayjs(insurancePolicyDetail?.fechaVigDesd)
      : null
  );
  const [vigenciaHasta, setVigenciaHasta] = useState<any>(
    insurancePolicyDetail?.fechaVigHast
      ? dayjs(insurancePolicyDetail?.fechaVigHast)
      : null
  );

  useEffect(() => {
    setVigenciaDesde(
      insurancePolicyDetail?.fechaVigDesd
        ? dayjs(insurancePolicyDetail?.fechaVigDesd)
        : null
    );
    setVigenciaHasta(
      insurancePolicyDetail?.fechaVigHast
        ? dayjs(insurancePolicyDetail?.fechaVigHast)
        : null
    );
    setDisabledButtons(insurancePolicyDetail?.estaVigente !== DECISION_TYPE.YES);
  }, [insurancePolicyDetail]);

  const { documentTypeList, genderList } = useContext(MasterContext);

  const getDefaultCoverageData = () =>
    possessionsList!.length > 0 &&
    Array.isArray((possessionsList![0] as LifePossession).coberturas)
      ? filterEnrollmentCoverages(
        (possessionsList![0] as LifePossession).coberturas
      ).map((cobertura) => ({
          nombre: cobertura.nombreCob,
          suma: formattedPrice(cobertura.sumaCob),
        }))
      : [];

  const getCoverageDataByInsured = (insured: Insured): CoberagePolicyEnrollmentAndTermination[] => {
    if (insured.coberturas?.length) {
      return insured.coberturas.map((cobertura) => ({
        nombre: cobertura.nombreCob,
        suma: formattedPrice(cobertura.sumaCob),
      }));
    }

    return getDefaultCoverageData();
  };

  const deleteInsured = (document: string) => {
    const deletedInsured = insureds.find((e) => e.documentNumber === document);

    if (deletedInsured?.newInsured) {
      const filteredInsureds = insureds.filter(
        (insured) => insured.documentNumber !== document
      );
      setInsureds(filteredInsureds);
    } else {
      setInsuredsMarkedForDeletion(
        (prev) => new Set(Array.from(prev).concat(document))
      );
      if (deletedInsured) {
        setInsuredsDeleteds((prev) => [...prev, deletedInsured]);
      }
    }
  };

  const cancelDeletion = (document: string) => {
    setInsuredsMarkedForDeletion((prev) => {
      const newSet = new Set(prev);
      newSet.delete(document);
      return newSet;
    });

    setInsuredsDeleteds((prev) =>
      prev.filter((insured) => insured.documentNumber !== document)
    );
  };

  function getValueByLabel(
    data: { label: string; value: string }[],
    label: string
  ): string | undefined {
    return data.find((item) => item.label === label)?.value;
  }

  const setInformation = (information: any) => {
    let data = information;

    if (
      insurancePolicyDetail?.nombreAsegurado &&
      insurancePolicyDetail?.nombreAsegurado !== ""
    ) {
      const name: any = {
        label: "Nombre",
        value: insurancePolicyDetail.nombreAsegurado,
      };
      data.person.unshift(name);
    }

    const documentFromPerson = getValueByLabel(data.person, "Documento");
    const documentValue = documentFromPerson == ": 0" ? getValueByLabel(data.person, "CUIT") : documentFromPerson;

    const client_data_info = [
      {
        label: "Apellido y Nombre",
        value: getValueByLabel(data.person, "Nombre"),
      },
      {
        label: "Nacionalidad",
        value: getValueByLabel(data.person, "Nacionalidad"),
      },
      {
        label: "Domicilio",
        value: getValueByLabel(data.address ?? [], "Domicilio"),
      },
      {
        label: "DNI / CUIT",
        value: documentValue,
      },
      {
        label: "Fecha de Nac.",
        value: getValueByLabel(data.person, "Fecha Nacimiento"),
      },
      {
        label: "Provincia",
        value: getValueByLabel(data.address ?? [], "Provincia"),
      },
      {
        label: "Condición IVA",
        value: getValueByLabel(data.person, "Condición Fiscal"),
      },
      {
        label: "Teléfono",
        value: getValueByLabel(data.communication ?? [], "Teléfono"),
      },
      {
        label: "Localidad",
        value: getValueByLabel(data.address ?? [], "Localidad"),
      },
      {
        label: "Tipo Persona",
        value: insurancePolicyDetail?.tipoPersona === "F" ? "PERSONA FÍSICA" : "PERSONA JURÍDICA",
      },
      {
        label: "Correo Electrónico",
        value: getValueByLabel(data.email ?? [], "Correo Electrónico"),
      },
    ];

    const beneficiaryNameValue = getValueByLabel(data.person, "Nombre") ?? "";
    const beneficiaryDocumentValue =
      getValueByLabel(data.person, "Documento") ?? "";

    setBeneficiaryName(beneficiaryNameValue);
    setBeneficiaryDocument(beneficiaryDocumentValue);

    setBeneficiaryPersonType(insurancePolicyDetail?.tipoPersona ?? "F")

    setClientDataForm(client_data_info);
    setInsured(data);
  };
  useEffect(() => {
    if (insuredsByPolicy) {
      setInformation(insuredsByPolicy);
    }
    /* eslint-disable-next-line */
  }, [insuredsByPolicy]);

  const changeBeneficiaryTaker = (isTaker: boolean) => {
    setBeneficiaryTaker(isTaker);
    if (isTaker) {
      setBeneficiaryName(insurancePolicyDetail!.nombreAsegurado ?? "");
      setBeneficiaryDocument(insurancePolicyDetail!.numeroDocAsegurado ?? "");
      setBeneficiaryPersonType(insurancePolicyDetail?.tipoPersona ?? "");
    } else {
      setBeneficiaryName("");
      setBeneficiaryDocument("");
      setBeneficiaryPersonType("");
    }
  };

  useEffect(() => {
    const beneficiaryData: Array<Column> = [
      {
        label: "Es Tomador",
        value: (
          <Checkbox
            checked={beneficiaryTaker}
            onChange={(e) => changeBeneficiaryTaker(e.target.checked)}
          >
            {beneficiaryTaker ? "SI" : "NO"}
          </Checkbox>
        ),
      },
      {
        label: "Nombre o Razón Social",
        value: (
          <Input
            value={beneficiaryName}
            onChange={(e) => setBeneficiaryName(e.target.value)}
            disabled={beneficiaryTaker}
          />
        ),
      },
      {
        label: "CUIT / DNI",
        value: (
          <Input
            value={beneficiaryDocument}
            onChange={(e) => setBeneficiaryDocument(e.target.value)}
            disabled={beneficiaryTaker}
          />
        ),
      },
      {
        label: "Cláusula de No Repetición",
        value: (
          <Checkbox
            checked={hasRepeticionClausule}
            onChange={(e) => setHasRepeticionClausule(e.target.checked)}
          >
            {hasRepeticionClausule ? "SI" : "NO"}
          </Checkbox>
        ),
      },
      {
        label: "Tipo de Persona",
        value: (
          <Select
            options={personTypeMock}
            value={beneficiaryPersonType || undefined}
            onChange={(value) => setBeneficiaryPersonType(value)}
            placeholder="Tipo de persona"
            disabled={beneficiaryTaker}
          />
        ),
      },
      {
        label: "A nombre de",
        columnSize: { xs: { span: 24 } },
        value: (
          <div>
            <Form.Item
              name="clause"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && value.trimLeft() !== value) {
                      const message = "No se permite un espacio al principio.";
                      return Promise.reject(message);
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Cláusula de no repetición"
                maxLength={7000}
                showCount
                value={clauseText}
                disabled={!hasRepeticionClausule}
                onChange={(e) => setClauseText(e.target.value)}
              />
            </Form.Item>
          </div>
        ),
      },
    ];

    setBeneficiaryDataForm(beneficiaryData);
  }, [
    beneficiaryTaker,
    beneficiaryName,
    beneficiaryDocument,
    hasRepeticionClausule,
    clauseText,
    insurancePolicyDetail?.tipoPersona,
  ]);

  const onPressAddInssured = (fieldValues: Insured) => {
    const genderLabel =
      genderList.find((gender) => gender.value === fieldValues.gender)?.label ||
      fieldValues.gender;
    const documentTypeLabel =
      fieldValues.documentType === "4"
        ? "DNI"
        : documentTypeList?.find(
          (documentType) => documentType.value === fieldValues.documentType
        )?.label || fieldValues.documentType;

    const firstLifePossession = possessionsList?.[0] as LifePossession | undefined;

    const newInsured: Insured = {
      fullName: fieldValues.fullName,
      documentType: documentTypeLabel,
      documentNumber: fieldValues.documentNumber,
      birthDate: dayjs(fieldValues.birthDate, "DD-MM-YYYY").format(
        "YYYY-MM-DD"
      ),
      cuil: fieldValues.cuil,
      gender: genderLabel,
      activities: fieldValues.activities,
      newInsured: true,
      coberturas: filterEnrollmentCoverages(firstLifePossession?.coberturas),
      sumAsegurada: firstLifePossession?.sumAseVida ?? "0",
    };

    setInsureds((prevState) => [...prevState, newInsured]);
  };

  const getAPActiviesListPerProduct = useCallback(
    async (article: string, branch: string, product: string) => {
      try {
        const response = await QuotationService.getAPActivitiesPerProduct(
          article,
          branch,
          product
        );

        if (response.data?.actividadesApPorProducto?.length) {
          const activities = response.data.actividadesApPorProducto.map(
            ({ codigo, descripcion }) => ({ label: descripcion, value: codigo })
          );
          setApActivitiesListOptions(activities);
        }
      } catch (error) { }
    },
    []
  );

  useEffect(() => {
    const list = (
      Array.isArray(possessionsList) ? possessionsList : []
    ) as LifePossession[];

    setInsureds((prevState) => {
      const userAdded = prevState.filter((i) => i.newInsured);
      const fromPolicy: Insured[] = list.map((e) => ({
        fullName: e.integrVida,
        documentType: e.tipDocVida,
        documentNumber: e.nroDocVida,
        birthDate: e.fecNacVida,
        cuil: e.numeroCuil,
        gender: e.genero,
        activities: e.actividad,
        newInsured: false,
        coberturas: e.coberturas ?? [],
        sumAsegurada: e.sumAseVida,
      }));
      return [...fromPolicy, ...userAdded];
    });
  }, [filterEnrollmentCoverages, possessionsList]);

  useEffect(() => {
    if (insurancePolicyDetail?.articulo && insurancePolicyDetail?.rama) {
      getAPActiviesListPerProduct(
        insurancePolicyDetail.articulo,
        insurancePolicyDetail.rama,
        insurancePolicyDetail.rama
      );
    }
  }, [
    insurancePolicyDetail?.articulo,
    insurancePolicyDetail?.rama,
    getAPActiviesListPerProduct,
  ]);

  const onPressGenerate = async () => {
    if (!insureds.length) return;

    const coberagesData: CoberagePolicyEnrollmentAndTermination[] = getDefaultCoverageData();
    const insuredLoaded: InsuredList[] = [];
    const insuredDeleteds: InsuredList[] = [];
    const insuredOfPolicy: InsuredList[] = [];
    let insuredPolicy: InsuredList;
    for (const insured of insureds) {
      if (insuredsMarkedForDeletion.has(insured.documentNumber)) {
        let insuredIterated: InsuredList;
        insuredIterated = {
          coberturas: getCoverageDataByInsured(insured),
          integrante: insured.fullName,
          numeroDocumento: insured.documentNumber,
          tipoDocumento: insured.documentType,
          nroCuilVida: insured.cuil,
          actividad: insured.activities,
          fechaNacimiento: dayjs(insured.birthDate).format("DD/MM/YYYY"),
          sexo: insured.gender,
          sumaAsegurada: insured.sumAsegurada ?? "0",
        };
        insuredDeleteds.push(insuredIterated);
      } else if (insured.newInsured) {
        let insuredIterated: InsuredList;
        insuredIterated = {
          coberturas: getCoverageDataByInsured(insured),
          integrante: insured.fullName,
          numeroDocumento: insured.documentNumber,
          tipoDocumento: insured.documentType,
          nroCuilVida: insured.cuil,
          actividad: insured.activities,
          fechaNacimiento: dayjs(insured.birthDate).format("DD/MM/YYYY"),
          sexo: insured.gender,
          sumaAsegurada: insured.sumAsegurada ?? "0",
        };
        insuredLoaded.push(insuredIterated);
      } else {
        insuredPolicy = {
          coberturas: getCoverageDataByInsured(insured),
          integrante: insured.fullName,
          numeroDocumento: insured.documentNumber,
          tipoDocumento: insured.documentType,
          nroCuilVida: insured.cuil,
          actividad: insured.activities,
          fechaNacimiento: dayjs(insured.birthDate).format("DD/MM/YYYY"),
          sexo: insured.gender,
          sumaAsegurada: insured.sumAsegurada ?? "0",
        };
        insuredOfPolicy.push(insuredPolicy);
      }
    }

    const request: getEnrollmentAndTerminationOfInsuredDocumentRequest = {
      nombreProductor: insurancePolicyDetail?.nombreProductor!,
      codigoProductor: insurancePolicyDetail?.codigoProductor!,
      numeroPoliza: insurancePolicyDetail?.nroPoliza!,
      nombreAsegurado: insurancePolicyDetail!.nombreAsegurado,
      domicilio: insured.address[1].value,
      fechaVigenciaDesde: vigenciaDesde.format("DD/MM/YYYY"),
      fechaVigenciaHasta: dayjs(insurancePolicyDetail?.fechaVigHast!).format(
        "DD/MM/YYYY"
      ),
      fechaYHora: dayjs().format("DD/MM/YYYY"),
      beneficiario: {
        nombre: beneficiaryName,
        cuit: beneficiaryDocument,
        tieneClausulaNoRepeticion: hasRepeticionClausule,
        clausulaNoRepeticion: clauseText,
      },
      coberturas: {
        nombre: coberagesData[0].nombre,
        suma: coberagesData[0].suma,
      },
      poliza: {
        numero: insurancePolicyDetail?.nroPoliza!,
        ramo: insurancePolicyDetail?.rama!,
      },
      listaAsegurados: insuredOfPolicy,
      altas: insuredLoaded,
      bajas: insuredDeleteds,
    };

    const filename = `PolizaDetalle_${insurancePolicyDetail?.nroPoliza}.pdf`;
    const response =
      await DocumentsService.getEnrollmentAndTerminationOfInsuredDocument(
        request
      );
    downloadBase64Files(response.data.base64, filename, FILE_TYPES.PDF);
    await invokeSendEmail(
      response.data.base64,
      filename,
      DistributionListKey.ET_INSURED,
      insuredOfPolicy,
      insuredDeleteds,
      insuredLoaded
    );
  };

  const invokeSendEmail = async (
    base64: string,
    fileName: string,
    distributionListKey: string,
    insuredOfPolicy: InsuredList[],
    insuredDeleteds: InsuredList[],
    insuredLoaded: InsuredList[],
    additionalRecipients?: Recipients[]
  ) => {
    try {
      // Se genera el HTML del body del mail con las tablas
      const bodyEmail = buildEmailBody(
        beneficiaryName,
        beneficiaryDocument,
        insuredOfPolicy,
        insuredDeleteds,
        insuredLoaded,
        apActiviesListOptions
      );

      const payload = await buildSendMailRequest(
        {
          subject: `AP Alta y Baja de Nómina Web ${insurancePolicyDetail?.nroPoliza}`,
          body: bodyEmail,
          includeAttachments: true,
          attachmentBase64: base64,
          attachmentName: fileName,
          attachmentMimeType: ContentTypeValue.PDF,
          saveToSentItems: true,
        },
        distributionListKey,
        additionalRecipients
      );
      if (payload) await EmailService.sendMailViaLambda(payload);
    } catch (error) {
      displayErrorMessage("Hubo un error al enviar el mail");
    }
  };

  const vigency_data: Array<Column> = [
    {
      label: "Vigencia Desde",
      value: (
        <DatePicker
          value={vigenciaDesde}
          format="DD/MM/YYYY"
          onChange={(date) => setVigenciaDesde(date)}
          style={{ width: "100%" }}
          minDate={dayjs()}
          maxDate={dayjs(vigenciaHasta)}
        />
      ),
    },
    {
      label: "Vigencia Hasta",
      value: (
        <DatePicker
          value={vigenciaHasta}
          format="DD/MM/YYYY"
          onChange={(date) => setVigenciaHasta(date)}
          style={{ width: "100%" }}
          disabled={true}
        />
      ),
    },
  ];

  const generateInsuredCardInfo = ({
    fullName,
    documentNumber,
    birthDate,
    cuil,
    gender,
    newInsured,
    coberturas,
  }: Insured): Array<Column> => {
    const baseData: Column[] = [
      {
        label: "Apellido y Nombre",
        value: fullName,
      },
      {
        label: "Documento",
        value: documentNumber,
      },
      {
        label: "Fecha Nacimiento",
        value: dayjs(birthDate).format("DD/MM/YYYY"),
      },
      {
        label: "CUIL",
        value: cuil,
      },
      {
        label: "Género",
        value: gender,
      },
    ];

    const coberturasData: Column[] =
      coberturas && Array.isArray(coberturas)
        ? coberturas.map((cobertura: any) => {
            return {
              label: cobertura.nombreCob,
              value: formattedPrice(cobertura.sumaCob),
            };
          })
        : [];

    const isMarkedForDeletion = insuredsMarkedForDeletion.has(documentNumber);
    // Se tiene que dejar siempre un asegurado sin eliminar como minimo
    const activeInsuredCount = insureds.filter(
      (i) => !insuredsMarkedForDeletion.has(i.documentNumber)
    ).length;
    const disableDelete = !isMarkedForDeletion && activeInsuredCount <= 1 || disabledButtons;
    const actionButton: Column = {
      label: "",
      value: (
        <ActionButton
          type="dashed"
          color={isMarkedForDeletion ? "secondaryDefault" : "danger"}
          label={isMarkedForDeletion ? "Cancelar" : "Eliminar"}
          icon={<DeleteOutlined />}
          onClick={() =>
            isMarkedForDeletion
              ? cancelDeletion(documentNumber)
              : deleteInsured(documentNumber)
          }
          disabled={disableDelete}
        />
      ),
    };

    return [...baseData, ...coberturasData, actionButton];
  };

  return (
    <div className="payroll-tab-container">
      <RowSize rowProps={{ gutter: [0, 0] }}>
        {isLoading ? (
          <SectionsSkeleton />
        ) : showError ? (
          <ErrorResults title="Hubo un error al intentar cargar los datos de la nómina" />
        ) : (
          <>
            <ColSize
              size={85}
              colProps={{ xs: { span: 21 }, sm: { span: 21 } }}
            >
              {clientDataForm
                ? _renderSection(
                  "Datos del Cliente o Tomador",
                  "datos-del-cliente",
                  clientDataForm
                )
                : null}

              {_renderSection("Vigencia", "vigencia", vigency_data)}

              {beneficiaryDataForm &&
                _renderSection(
                  "Datos del beneficiario",
                  "datos-del-beneficiario",
                  beneficiaryDataForm
                )}

              <Typography.Title
                id="datos-del-asegurado"
                level={4}
                style={{
                  margin: 0,
                  marginBottom: 10,
                  marginTop: 20,
                  color: COLORS.principal[100],
                }}
              >
                Datos del asegurado
              </Typography.Title>

              {insureds.length ? (
                insureds.map((item, i) =>
                  _renderInsuredCards(generateInsuredCardInfo(item), i)
                )
              ) : (
                <Typography.Text>
                  La póliza {insurancePolicyDetail?.nroPoliza} no contiene
                  asegurados.
                </Typography.Text>
              )}

              <Divider style={{ minWidth: "90%", width: "98%" }} />

              <Typography.Title
                id="alta-de-asegurado"
                level={4}
                style={{
                  margin: 0,
                  marginBottom: 10,
                  marginTop: 20,
                  color: COLORS.principal[100],
                }}
              >
                Alta de asegurado
              </Typography.Title>

              <InsuredRegistrationForm
                insurancePolicyDetail={insurancePolicyDetail}
                policyProductCode={
                  (possessionsList?.[0] as LifePossession | undefined)?.codProducVida ??
                  (possessionsList?.[0] as LifePossession | undefined)?.producVida
                }
                onGenerate={onPressGenerate}
                onSubmit={onPressAddInssured}
                disabledButtons={disabledButtons}
              />
            </ColSize>

            <ColSize
              size={9}
              colProps={{
                id: "payroll-tab-container-anchor",
                xs: { span: 3 },
                sm: { span: 3 },
              }}
            >
              <Anchor
                items={MOCK_SECTIONS}
                containerId="payroll-tab-container-anchor"
              />
            </ColSize>
          </>
        )}
      </RowSize>
    </div>
  );
};
